import { createSlice } from "@reduxjs/toolkit";
import {
  createTreatmentPlan,
  fetchTreatmentPlans,
  fetchTreatmentPlanById,
  updateTreatmentPlan,
  deleteTreatmentPlan,
  fetchClientTreatmentPlans,
  fetchLatestTreatmentPlan,
  autosaveTreatmentPlan,
  fetchMyDraftTreatmentPlans
} from "../thunks/treatmentPlanThunk";

const initialState = {
  treatmentPlans: [],
  currentTreatmentPlan: null,
  latestTreatmentPlan: null,
  clientTreatmentPlans: {
    client: null,
    treatmentPlans: []
  },
  // Autosave/Draft state
  drafts: [],
  autosaveId: null, // Track the ID of the current draft being edited
  autosaveStatus: 'idle', // 'idle' | 'saving' | 'saved' | 'error'
  lastAutosaved: null,
  pagination: {
    totalDocs: 0,
    limit: 10,
    totalPages: 0,
    page: 1,
    pagingCounter: 0,
    hasPrevPage: false,
    hasNextPage: false,
    prevPage: null,
    nextPage: null
  },
  loading: false,
  error: null,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false
};

const treatmentPlanSlice = createSlice({
  name: "treatmentPlan",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentTreatmentPlan: (state) => {
      state.currentTreatmentPlan = null;
    },
    clearClientTreatmentPlans: (state) => {
      state.clientTreatmentPlans = {
        client: null,
        treatmentPlans: []
      };
    },
    clearLatestTreatmentPlan: (state) => {
      state.latestTreatmentPlan = null;
    },
    setCurrentTreatmentPlan: (state, action) => {
      state.currentTreatmentPlan = action.payload;
    },
    resetLoadingStates: (state) => {
      state.createLoading = false;
      state.updateLoading = false;
      state.deleteLoading = false;
      state.loading = false;
    },
    setAutosaveId: (state, action) => {
      state.autosaveId = action.payload;
    },
    clearAutosaveState: (state) => {
      state.autosaveId = null;
      state.autosaveStatus = 'idle';
      state.lastAutosaved = null;
    },
    clearDrafts: (state) => {
      state.drafts = [];
    }
  },
  extraReducers: (builder) => {
    // Create treatment plan
    builder
      .addCase(createTreatmentPlan.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createTreatmentPlan.fulfilled, (state, action) => {
        state.createLoading = false;
        state.treatmentPlans.unshift(action.payload.treatmentPlan);
        state.currentTreatmentPlan = action.payload.treatmentPlan;
      })
      .addCase(createTreatmentPlan.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload?.message || "Failed to create treatment plan";
      });

    // Fetch all treatment plans
    builder
      .addCase(fetchTreatmentPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTreatmentPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.treatmentPlans = action.payload.docs || action.payload;

        // Handle pagination if present
        if (action.payload.docs) {
          state.pagination = {
            totalDocs: action.payload.totalDocs,
            limit: action.payload.limit,
            totalPages: action.payload.totalPages,
            page: action.payload.page,
            pagingCounter: action.payload.pagingCounter,
            hasPrevPage: action.payload.hasPrevPage,
            hasNextPage: action.payload.hasNextPage,
            prevPage: action.payload.prevPage,
            nextPage: action.payload.nextPage
          };
        }
      })
      .addCase(fetchTreatmentPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch treatment plans";
      });

    // Fetch treatment plan by ID
    builder
      .addCase(fetchTreatmentPlanById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTreatmentPlanById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTreatmentPlan = action.payload;
      })
      .addCase(fetchTreatmentPlanById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch treatment plan";
      });

    // Update treatment plan
    builder
      .addCase(updateTreatmentPlan.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateTreatmentPlan.fulfilled, (state, action) => {
        state.updateLoading = false;
        const updatedPlan = action.payload.treatmentPlan;

        // Update in the main list
        const index = state.treatmentPlans.findIndex(
          plan => plan._id === updatedPlan._id
        );
        if (index !== -1) {
          state.treatmentPlans[index] = updatedPlan;
        }

        // Update current plan if it's the same one
        if (state.currentTreatmentPlan?._id === updatedPlan._id) {
          state.currentTreatmentPlan = updatedPlan;
        }

        // Update in client treatment plans if present
        if (state.clientTreatmentPlans?.treatmentPlans?.docs) {
          const clientPlanIndex = state.clientTreatmentPlans.treatmentPlans.docs.findIndex(
            plan => plan._id === updatedPlan._id
          );
          if (clientPlanIndex !== -1) {
            state.clientTreatmentPlans.treatmentPlans.docs[clientPlanIndex] = updatedPlan;
          }
        }
      })
      .addCase(updateTreatmentPlan.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload?.message || "Failed to update treatment plan";
      });

    // Delete treatment plan
    builder
      .addCase(deleteTreatmentPlan.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteTreatmentPlan.fulfilled, (state, action) => {
        state.deleteLoading = false;
        const deletedId = action.payload.treatmentPlanId;

        // Remove from main list
        state.treatmentPlans = state.treatmentPlans.filter(
          plan => plan._id !== deletedId
        );

        // Clear current plan if it's the deleted one
        if (state.currentTreatmentPlan?._id === deletedId) {
          state.currentTreatmentPlan = null;
        }

        // Remove from client treatment plans if present
        if (state.clientTreatmentPlans.treatmentPlans.docs) {
          state.clientTreatmentPlans.treatmentPlans.docs =
            state.clientTreatmentPlans.treatmentPlans.docs.filter(
              plan => plan._id !== deletedId
            );
        }
      })
      .addCase(deleteTreatmentPlan.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload?.message || "Failed to delete treatment plan";
      });

    // Fetch client treatment plans
    builder
      .addCase(fetchClientTreatmentPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientTreatmentPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.clientTreatmentPlans = action.payload;
      })
      .addCase(fetchClientTreatmentPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch client treatment plans";
      });

    // Fetch latest treatment plan
    builder
      .addCase(fetchLatestTreatmentPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLatestTreatmentPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.latestTreatmentPlan = action.payload.treatmentPlan;
      })
      .addCase(fetchLatestTreatmentPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch latest treatment plan";
        state.latestTreatmentPlan = null;
      });

    // Autosave treatment plan
    builder
      .addCase(autosaveTreatmentPlan.pending, (state) => {
        state.autosaveStatus = 'saving';
      })
      .addCase(autosaveTreatmentPlan.fulfilled, (state, action) => {
        state.autosaveStatus = 'saved';
        state.lastAutosaved = new Date().toISOString();
        // Store the ID so subsequent autosaves update the same draft
        const savedPlan = action.payload.treatmentPlan;
        if (savedPlan?._id) {
          state.autosaveId = savedPlan._id;
        }
      })
      .addCase(autosaveTreatmentPlan.rejected, (state, action) => {
        state.autosaveStatus = 'error';
        // Don't show error for autosave failures - silent failure is preferred
        console.error('[TreatmentPlan Slice] Autosave failed:', action.payload);
      });

    // Fetch my drafts
    builder
      .addCase(fetchMyDraftTreatmentPlans.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMyDraftTreatmentPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.drafts = action.payload.drafts || [];
      })
      .addCase(fetchMyDraftTreatmentPlans.rejected, (state, action) => {
        state.loading = false;
        state.drafts = [];
      });
  },
});

export const {
  clearError,
  clearCurrentTreatmentPlan,
  clearClientTreatmentPlans,
  clearLatestTreatmentPlan,
  setCurrentTreatmentPlan,
  resetLoadingStates,
  setAutosaveId,
  clearAutosaveState,
  clearDrafts
} = treatmentPlanSlice.actions;

export default treatmentPlanSlice.reducer;
